import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { Switch } from '@/design-system/switch';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import {
  ISO_DAY_LABELS,
  PitstopScheduleBoardRow,
  PitstopScheduleError,
  pitstopSchedulesAPI,
} from '@/services/api/pitstopSchedules';
import { formatDateTimeLong } from '@/utils';
import { shiftActiveDays, slotToLocal } from '@/utils/scheduleTime';

import AddScheduleModal from './components/AddScheduleModal';
import { QUEUE_STATUS_LABELS, QUEUE_STATUS_TONE } from './queueStatus';

/**
 * A board row on the viewer's clock. Slots are stored as UTC wall-clock, and
 * crossing zones can push one across midnight — so the active days move with
 * the time, never independently of it.
 */
interface BoardViewRow extends PitstopScheduleBoardRow {
  localTime: string;
  localActiveDays: number[];
}

const toViewRow = (row: PitstopScheduleBoardRow): BoardViewRow => {
  const { time, dayShift } = slotToLocal(row.dispatchTime, row.timezone);
  return {
    ...row,
    localTime: time,
    localActiveDays: shiftActiveDays(row.activeDays, dayShift),
  };
};

/** Sen…Min pills; the inactive ones stay visible but muted. */
const DayRow: React.FC<{ days: number[] }> = ({ days }) => (
  <div className="flex flex-nowrap gap-1 whitespace-nowrap">
    {ISO_DAY_LABELS.map((label, i) => {
      const on = days.includes(i + 1);
      return (
        <span
          key={label}
          className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
            on
              ? 'bg-primary-alpha-16 text-primary-base'
              : 'bg-bg-weak-50 text-text-disabled-300'
          }`}
        >
          {label}
        </span>
      );
    })}
  </div>
);

const DispatchSchedulesPage: React.FC = () => {
  const [rows, setRows] = useState<PitstopScheduleBoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await pitstopSchedulesAPI.listAll());
    } catch (err) {
      setRows([]);
      toast.error('Gagal memuat jadwal dispatch', {
        description: (err as PitstopScheduleError)?.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const viewRows = useMemo(() => rows.map(toViewRow), [rows]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.isActive).length,
      pitstops: new Set(rows.map((r) => r.pitstopId)).size,
    }),
    [rows]
  );

  const toggleActive = async (row: BoardViewRow) => {
    setBusyId(row.id);
    // Optimistic: the switch should answer instantly; a failure rolls back.
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, isActive: !r.isActive } : r))
    );
    try {
      await pitstopSchedulesAPI.update(row.id, { isActive: !row.isActive });
      toast.success(
        `Jadwal ${row.localTime} ${!row.isActive ? 'diaktifkan' : 'dinonaktifkan'}`
      );
    } catch (err) {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isActive: row.isActive } : r))
      );
      toast.error('Gagal mengubah status jadwal', {
        description: (err as PitstopScheduleError)?.message,
      });
    } finally {
      setBusyId(null);
    }
  };

  const removeRow = async (row: BoardViewRow) => {
    setBusyId(row.id);
    try {
      await pitstopSchedulesAPI.remove(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(`Jadwal ${row.localTime} dihapus`);
    } catch (err) {
      const e = err as PitstopScheduleError;
      toast.error(
        e?.code === 'SCHEDULE_IN_USE'
          ? 'Jadwal sudah pernah dipakai queue — nonaktifkan saja'
          : 'Gagal menghapus jadwal',
        { description: e?.message }
      );
    } finally {
      setBusyId(null);
    }
  };

  const columns: TableDataColumn<BoardViewRow>[] = [
    {
      key: 'pitstop',
      title: 'Hub / Pitstop',
      render: (_v, row) => (
        <div>
          <div className="font-semibold text-text-strong-950">
            {row.pitstopName}
          </div>
          <div className="text-[11px] text-text-sub-600">Hub Client</div>
        </div>
      ),
    },
    { key: 'client', title: 'Client', render: (_v, row) => row.clientName },
    {
      key: 'time',
      title: 'Jam',
      width: '110px',
      render: (_v, row) => (
        <span className="font-mono text-[15px] font-semibold tabular-nums text-text-strong-950">
          {row.localTime}
        </span>
      ),
    },
    {
      key: 'days',
      title: 'Hari aktif',
      // Wide enough that the seven pills stay on one line, like the prototype.
      width: '230px',
      render: (_v, row) => <DayRow days={row.localActiveDays} />,
    },
    {
      key: 'lastQueue',
      title: 'Queue terakhir',
      render: (_v, row) =>
        row.lastQueue ? (
          <div>
            <Badge status={QUEUE_STATUS_TONE[row.lastQueue.status]} size="sm">
              {QUEUE_STATUS_LABELS[row.lastQueue.status]}
            </Badge>
            <div className="mt-0.5 text-[11px] text-text-sub-600">
              {row.lastQueue.dispatchId && row.lastQueue.dispatchCode ? (
                <Link
                  to={`/dispatches/${row.lastQueue.dispatchId}`}
                  className="font-semibold text-primary-base hover:underline"
                >
                  {row.lastQueue.dispatchCode}
                </Link>
              ) : (
                (row.lastQueue.error ?? '—')
              )}
              {' · '}
              {formatDateTimeLong(row.lastQueue.scheduledAt)}
            </div>
            {row.lastQueue.status === 'FAILED' && (
              <div className="mt-1 max-w-[280px]">
                <div className="truncate text-[11px] font-medium text-error-base">
                  {row.lastQueue.error ?? 'Gagal'}
                </div>
                {row.lastQueue.errorDetail && (
                  // Full stage/message/stack, collapsed by default so the row
                  // stays scannable but the trace is one click away.
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-[11px] font-semibold text-text-sub-600 hover:text-text-strong-950">
                      Lihat trace
                    </summary>
                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-bg-weak-50 p-2 text-[10px] leading-relaxed text-text-sub-600">
                      {row.lastQueue.errorDetail}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-text-sub-600">—</span>
        ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, row) =>
        row.isActive ? (
          <Badge status="success" size="sm">
            Aktif
          </Badge>
        ) : (
          <Badge status="faded" size="sm">
            Nonaktif
          </Badge>
        ),
    },
    {
      key: 'actions',
      title: 'Aksi',
      width: '110px',
      align: 'right',
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-3">
          <Switch
            checked={row.isActive}
            disabled={busyId === row.id}
            onCheckedChange={() => void toggleActive(row)}
            aria-label={row.isActive ? 'Nonaktifkan jadwal' : 'Aktifkan jadwal'}
          />
          <button
            type="button"
            title="Hapus jadwal"
            disabled={busyId === row.id}
            onClick={() => void removeRow(row)}
            className="rounded-lg border border-stroke-soft-200 p-1.5 text-text-sub-600 transition-colors hover:border-error-base hover:text-error-base disabled:opacity-50"
          >
            <i className="ri-delete-bin-line text-[16px]" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-text-sub-600">
            03·1 · Hub Ops
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-text-strong-950">
            Jadwal Dispatch
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-text-sub-600">
            Dispatch berjalan otomatis per <b>pitstop client</b> pada jam yang
            disetel.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button tone="neutral" style="stroke" onClick={() => void fetchRows()}>
            <i className="ri-refresh-line mr-1.5" />
            Refresh
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <i className="ri-add-line mr-1.5" />
            Tambah jadwal
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3 text-[12.5px] text-text-sub-600">
        <i className="ri-information-line mt-0.5 text-[15px]" />
        <div>
          <b className="text-text-strong-950">Cara kerja:</b> saat ops meng-import
          shipment untuk sebuah pitstop, jam di sini yang bisa dipilih sebagai
          waktu kirim. Dispatch langsung dibuat begitu upload selesai — upload
          berikutnya untuk jam yang sama menambah ke dispatch yang sudah ada —
          lalu di-commit tepat di jam tersebut. Zona &amp; rider mengikuti
          konfigurasi pitstop.
          Jam disimpan dalam UTC dan ditampilkan mengikuti zona waktu perangkat
          kamu.
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: 'Total jadwal', value: stats.total, tone: '' },
          {
            label: 'Aktif',
            value: stats.active,
            tone: 'text-success-base',
          },
          { label: 'Pitstop client', value: stats.pitstops, tone: '' },
        ].map((s) => (
          <Card key={s.label} className="px-5 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-sub-600">
              {s.label}
            </div>
            <div
              className={`mt-1 text-3xl font-bold tracking-tight ${s.tone || 'text-text-strong-950'}`}
            >
              {s.value}
            </div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <TableData<BoardViewRow>
          data={viewRows}
          columns={columns}
          loading={loading}
          emptyState={
            <div className="px-4 py-10 text-center">
              <div className="font-semibold text-text-strong-950">
                Belum ada jadwal dispatch
              </div>
              <div className="mt-1 text-[12.5px] text-text-sub-600">
                Tambah jadwal agar dispatch berjalan otomatis per pitstop pada
                jam yang disetel.
              </div>
            </div>
          }
        />
      </Card>

      <AddScheduleModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          void fetchRows();
        }}
      />
    </div>
  );
};

export default DispatchSchedulesPage;
